import React from 'react';

import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { GeneralBlockHead, GeneralBlockHeader, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';

const SectionLink = ({ icon, title, to }: { icon: IconProp; title: string; to: string }) => {
    return (
        <Link
            className="flex h-auto gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left font-normal hover:bg-gray-100 hover:fill-gray-900 hover:text-gray-900 dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800 dark:hover:text-gray-50"
            to={to}
        >
            <GeneralBlockHeader>
                <SquareIcon icon={icon} />
                <GeneralBlockHead>
                    <GeneralBlockTitle className="text-base text-gray-900">{title}</GeneralBlockTitle>
                </GeneralBlockHead>
            </GeneralBlockHeader>
        </Link>
    );
};

export default function StatisticOptions() {
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(statisticMessages.statistics));
        return removeTitle;
    }, []);

    const accessPaymentsStatistics = useHasPermissions([PERMISSION.ACCESS_PAYMENT_STATISTICS]);
    const accessOccupancyStatistics = useHasPermissions([PERMISSION.ACCESS_OCCUPANCY_STATISTICS]);
    const accessValidationStatistics = useHasPermissions([PERMISSION.ACCESS_VALIDATION_STATISTICS]);
    const accessEventLogs = useHasPermissions([PERMISSION.ACCESS_EVENT_LOGS]);

    return (
        <>
            {accessPaymentsStatistics && (
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockHead>
                            <SectionBlockTitle>{formatMessage(statisticMessages.payments)}</SectionBlockTitle>
                        </SectionBlockHead>
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <SectionBlockBody className="grid md:grid-cols-3">
                        <SectionLink
                            to="payment"
                            icon={ICON_MAP.COINS}
                            title={formatMessage(statisticMessages.paymentStatistics)}
                        />
                        <SectionLink
                            to="monthly-payment"
                            icon={ICON_MAP.CIRCLE_EURO}
                            title={formatMessage(statisticMessages.monthlyPaymentBreakdown)}
                        />
                    </SectionBlockBody>
                </SectionBlock>
            )}
            {accessOccupancyStatistics && (
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockHead>
                            <SectionBlockTitle>{formatMessage(statisticMessages.occupancy)}</SectionBlockTitle>
                        </SectionBlockHead>
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <SectionBlockBody className="grid md:grid-cols-3">
                        <SectionLink
                            to="visit"
                            icon={ICON_MAP.CLIENT}
                            title={formatMessage(statisticMessages.visitStatistics)}
                        />
                        <SectionLink
                            to="capacity"
                            icon={ICON_MAP.USERS}
                            title={formatMessage(statisticMessages.capacityOverview)}
                        />
                        <SectionLink
                            to="gateway"
                            icon={ICON_MAP.ARROW_RIGHT_ARC}
                            title={formatMessage(statisticMessages.gatewayStatistics)}
                        />
                    </SectionBlockBody>
                </SectionBlock>
            )}
            {accessValidationStatistics && (
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockHead>
                            <SectionBlockTitle>{formatMessage(statisticMessages.operations)}</SectionBlockTitle>
                        </SectionBlockHead>
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <SectionBlockBody className="grid md:grid-cols-3">
                        <SectionLink
                            to="validation"
                            icon={ICON_MAP.TICKET_PERFORATED}
                            title={formatMessage(statisticMessages.validations)}
                        />
                    </SectionBlockBody>
                </SectionBlock>
            )}
            {accessEventLogs && (
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockHead>
                            <SectionBlockTitle>{formatMessage(statisticMessages.events)}</SectionBlockTitle>
                        </SectionBlockHead>
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <SectionBlockBody className="grid md:grid-cols-3">
                        <SectionLink
                            to="user-actions"
                            icon={ICON_MAP.USERS}
                            title={formatMessage(statisticMessages.userActions)}
                        />
                    </SectionBlockBody>
                </SectionBlock>
            )}
        </>
    );
}
